import { useCallback, useState } from 'react';
import { getParsedFilesList, isValidFileExtension } from '@/libs/file';
import { showWarningToast } from '@/libs/toast/toast';
import { CONTENT_FORMATS } from '@/Pages/Sessions/NewContentModal/constants';

export default function useFilesDragNDrop({
  onFilesUpload,
  uploadedFiles = [],
  accept = CONTENT_FORMATS
}: {
  onFilesUpload: (files: File[]) => void;
  uploadedFiles?: File[];
  accept?: string;
}) {
  const [isDragging, setIsDragging] = useState(false);

  const handleFilesToUpload = useCallback(
    (filesToUpload: File[]) => {
      const newFiles = filesToUpload.filter(file => !uploadedFiles.find(f => f.name === file.name));
      onFilesUpload(newFiles);
    },
    [uploadedFiles, onFilesUpload]
  );

  const getValidatedFiles = useCallback(
    (fileList: FileList): File[] => {
      const files = getParsedFilesList(fileList);
      const filesToUpload = files.filter(file => {
        return isValidFileExtension(file.name, accept);
      });
      if (filesToUpload.length < files.length) {
        showWarningToast(`Supported file formats are: ${accept.replaceAll('.', '').toUpperCase().split(', ')}`);
      }
      return filesToUpload;
    },
    [accept]
  );

  const getFilesFromDrop = useCallback(
    (ev: React.DragEvent): File[] => {
      if (!ev.dataTransfer.files || !ev.dataTransfer.files.length) {
        return [];
      }
      return getValidatedFiles(ev.dataTransfer.files);
    },
    [getValidatedFiles]
  );

  const getFilesFromInput = useCallback(
    (ev: React.ChangeEvent): File[] => {
      const inputElement = ev.currentTarget as HTMLInputElement;
      if (!inputElement.files || !inputElement.files.length) {
        return [];
      }
      return getValidatedFiles(inputElement.files);
    },
    [getValidatedFiles]
  );

  const uploadFiles = useCallback(
    (ev: React.DragEvent | React.ChangeEvent) => {
      ev.preventDefault();
      setIsDragging(false);
      let filesToUpload: File[] = [];
      if (ev.type === 'drop') {
        filesToUpload = getFilesFromDrop(ev as React.DragEvent);
      } else if (ev.currentTarget instanceof HTMLInputElement) {
        filesToUpload = getFilesFromInput(ev as React.ChangeEvent);
      }
      if (filesToUpload.length) {
        handleFilesToUpload(filesToUpload);
      }
    },
    [getFilesFromDrop, getFilesFromInput, handleFilesToUpload]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDragging) {
        setIsDragging(true);
      }
    },
    [isDragging]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only consider it a leave if we're leaving the drop target completely
    // This checks if we're moving to a child element or completely out
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;

    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  return {
    isDragging,
    handleFilesToUpload,
    uploadFiles,
    handleDragEnter,
    handleDragOver,
    handleDragLeave
  };
}
